import { useState } from 'react'
import { X, DollarSign, Loader2, AlertTriangle } from 'lucide-react'
import { usePayOut, useCreateReceipt } from '@/api/receipts'
import { usePOSStore } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'

export function PayoutModal() {
  const { closeModal, showToast } = useUIStore()
  const { currentReceiptId, selectedCustomer, setReceiptId } = usePOSStore()

  const [amount, setAmount] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')

  const payOut = usePayOut()
  const createReceipt = useCreateReceipt()

  const isPending = payOut.isPending || createReceipt.isPending
  const amountNum = parseFloat(amount) || 0

  const ensureReceiptExists = async (): Promise<number> => {
    if (currentReceiptId) return currentReceiptId
    const receipt = await createReceipt.mutateAsync({ customer_id: selectedCustomer?.id })
    setReceiptId(receipt.id)
    return receipt.id
  }

  const handlePayout = async () => {
    if (isPending) return
    setError('')

    if (amountNum <= 0) {
      setError('Enter a valid payout amount.')
      return
    }

    try {
      const receiptId = await ensureReceiptExists()
      await payOut.mutateAsync({ receiptId, amount: amountNum, notes: notes.trim() || undefined })
      closeModal()
      showToast({ type: 'success', title: 'Payout Complete', message: `$${amountNum.toFixed(2)} paid out from drawer.` })
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } }
      const msg = axiosErr?.response?.data?.error
        || (err instanceof Error ? err.message : 'Payout failed.')
      // 400 likely means cashier doesn't have can_po permission
      if (msg.toLowerCase().includes('permission') || msg.toLowerCase().includes('access') || msg.toLowerCase().includes('400')) {
        setError('You do not have permission to perform payouts.')
      } else {
        setError(msg)
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
      <div className="absolute inset-0 bg-black/50" onClick={!isPending ? closeModal : undefined} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col animate-scale-in">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <DollarSign size={20} className="text-emerald-500" />
            <h2 className="text-lg font-semibold text-gray-900">Cash Payout</h2>
          </div>
          {!isPending && (
            <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
              <X size={20} className="text-gray-500" />
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">This will remove cash from the drawer and create an audit record.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Payout Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setError('') }}
                onKeyDown={(e) => e.key === 'Enter' && handlePayout()}
                placeholder="0.00"
                disabled={isPending}
                className="w-full pl-7 pr-4 py-3 text-xl font-bold border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:ring-0 outline-none disabled:bg-gray-50"
                autoFocus
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for payout..."
              disabled={isPending}
              rows={2}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:ring-0 outline-none resize-none text-sm disabled:bg-gray-50"
            />
          </div>

          {error && (
            <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}

          <div className="flex gap-3">
            <button
              onClick={closeModal}
              disabled={isPending}
              className="flex-1 py-3 border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handlePayout}
              disabled={amountNum <= 0 || isPending}
              className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
            >
              {isPending && <Loader2 size={16} className="animate-spin" />}
              {isPending ? 'Processing...' : 'Confirm Payout'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
